import React, { useState, useEffect, useRef } from 'react';
import { collection, query, orderBy, limit, onSnapshot, doc, updateDoc, arrayUnion, arrayRemove, getDoc, setDoc, addDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db, storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { processTweetData } from '../utils/tweetProcessor';
import BackgroundSelector from './BackgroundSelector';

// Add your Firebase Storage bucket name here
const FIREBASE_AUDIO_BUCKET = 'twizzler';

const TwitterImporter = ({ onTweetImported, isGenerating, user }) => {
  const [tweetUrl, setTweetUrl] = useState('');
  const [additionalUrls, setAdditionalUrls] = useState([]); // Additional URL fields
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingUsername, setPendingUsername] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [userStats, setUserStats] = useState({
    totalVideos: 0,
    avgGenerationTime: '~30s',
    recentImports: []
  });
  
  // Sample mode state
  const [showSampleConfirmation, setShowSampleConfirmation] = useState(false);
  const [pendingSampleUsername, setPendingSampleUsername] = useState('');
  
  // Duplicate video detection state
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [pendingDuplicateUrl, setPendingDuplicateUrl] = useState('');
  
  // Audio selection state
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [selectedAudioType, setSelectedAudioType] = useState('default'); // 'default', 'custom', 'library'
  const [selectedAudioFile, setSelectedAudioFile] = useState(null);
  const [selectedLibraryAudio, setSelectedLibraryAudio] = useState('');
  const [customAudioFile, setCustomAudioFile] = useState(null);
  const [availableLibraryAudio, setAvailableLibraryAudio] = useState([]);
  const [userCustomAudio, setUserCustomAudio] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('trending');

  const [playingAudioId, setPlayingAudioId] = useState(null);
  
  // Starred audio state
  const [starredAudio, setStarredAudio] = useState([]);
  
  // Circle overlay state
  const [showCircleModal, setShowCircleModal] = useState(false);
  const [selectedCircles, setSelectedCircles] = useState([]);
  const [availableCircles, setAvailableCircles] = useState([]);
  
  // Background selection state
  const [selectedBackground, setSelectedBackground] = useState({ type: 'black' });
  const [showBackgroundModal, setShowBackgroundModal] = useState(false);
  
  const audioRef = useRef(null);

  useEffect(() => {
    if (!user) return;

    // Subscribe to user's profile document
    const userProfileRef = doc(db, 'users', user.uid);
    
    const unsubscribe = onSnapshot(userProfileRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setUserProfile(data);
        setUserStats({
          totalVideos: data.totalVideos || 0,
          avgGenerationTime: '~30s',
          recentImports: data.recentImports || []
        });
      } else {
        // Initialize user profile if it doesn't exist
        const initialProfile = {
          totalVideos: 0,
          recentImports: [],
          twitterUsernames: [],
          sampleUsernames: [],
          isUnlimited: false,
          createdAt: new Date().toISOString()
        };
        setDoc(userProfileRef, initialProfile);
        setUserProfile(initialProfile);
      }
    });

    return () => unsubscribe();
  }, [user]);

  // Load available circle overlays
  useEffect(() => {
    const loadCircleOverlays = async () => {
      try {
        const response = await fetch('/api/video/circle-overlays');
        if (response.ok) {
          const data = await response.json();
          setAvailableCircles(data.circles || []);
        }
      } catch (error) {
        console.error('Error loading circle overlays:', error);
      }
    };

    loadCircleOverlays();
  }, []);

  // Load user's custom audio library
  useEffect(() => {
    if (!user) return;

    const customAudioRef = collection(db, 'users', user.uid, 'customAudio');
    const q = query(customAudioRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const customAudioList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        isCustom: true
      }));
      setUserCustomAudio(customAudioList);
    });

    return () => unsubscribe();
  }, [user]);

  // Load available library audio files with categories
  useEffect(() => {
    const loadLibraryAudio = async () => {
      try {
        setAvailableLibraryAudio([
          // Trending
          { id: 'grave', name: 'GRAVE', filename: 'GRAVE.MP3', category: 'trending' },
          { id: 'wush_2', name: 'WUSH 2', filename: 'WUSH 2.mp3', category: 'trending' },
          { id: 'promises', name: 'PROMISES', filename: 'PROMISES.mp3', category: 'trending' },
          { id: 'flip_flop_fliep_flap', name: 'Flip Flop Fliep Flap', filename: 'Flip Flop Fliep Flap.mp3', category: 'trending' },
          { id: 'no_other_demo_heart', name: 'No Other Demo Heart', filename: 'No Other Demo Heart.mp3', category: 'trending' },
          { id: 'somebody', name: 'Somebody', filename: 'Somebody.mp3', category: 'trending' },
          { id: 'believe', name: 'Believe', filename: 'Believe.mp3', category: 'trending' },
          { id: 'doomsday', name: 'Doomsday', filename: 'doomsday.mp3', category: 'trending' },
          { id: 'seteadora', name: 'SETEADORA', filename: 'SETEADORA.mp3', category: 'trending' },
          { id: 'rules', name: 'Rules', filename: 'Rules.mp3', category: 'trending' },
          { id: 'el_tiki_ta', name: 'El Tiki-Ta', filename: 'El Tiki-Ta.mp3', category: 'trending' },
          { id: 'la_boda', name: 'La Boda', filename: 'La Boda.mp3', category: 'trending' },
          { id: 'unslept', name: 'Unslept', filename: 'Unslept.mp3', category: 'trending' },
          { id: 'i_know', name: 'I Know', filename: 'I Know.mp3', category: 'trending' },
          { id: 'last_kiss', name: 'Last Kiss', filename: 'Last Kiss.mp3', category: 'trending' },
          { id: 'neon_guts', name: 'Neon Guts', filename: 'Neon Guts.mp3', category: 'trending' },
          { id: 'piano_tiles', name: 'Piano Tiles', filename: 'Piano Tiles.mp3', category: 'trending' },
          { id: 'yellow_cab', name: 'Yellow Cab', filename: 'Yellow Cab.mp3', category: 'trending' },
          { id: 'cant_go_broke_remix', name: 'Cant Go Broke (Remix)', filename: 'Cant Go Broke (Remix).mp3', category: 'trending' },
          { id: 'amnesia', name: 'Amnesia', filename: 'Amnesia.mp3', category: 'trending' },
          { id: 'serdce', name: 'Сердце', filename: 'Сердце.mp3', category: 'trending' },
          { id: 'cant_stop_the_feeling', name: 'CAN\'T STOP THE FEELING!', filename: 'CAN\'T STOP THE FEELING!.mp3', category: 'trending' },
          { id: 'my_own_hymn', name: 'My Own Hymn', filename: 'My Own Hymn.mp3', category: 'trending' },
          
          // Chill
          { id: 'all_i_need', name: 'ALL I NEED', filename: 'ALL I NEED.mp3', category: 'chill' },
          { id: 'dream', name: 'DREAM', filename: 'DREAM.mp3', category: 'chill' },
          { id: 'nostalgia', name: 'NOSTALGIA', filename: 'NOSTALGIA.mp3', category: 'chill' },
          { id: 'void_in_blue', name: 'VOID IN BLUE', filename: 'VOID IN BLUE.mp3', category: 'chill' },
          { id: 'horizonte_de_diamante', name: 'HORIZONTE DE DIAMENTE', filename: 'HORIZONTE DE DIAMENTE.mp3', category: 'chill' },
          { id: 'horizonte_ultra_slowed', name: 'HORIZONTE ULTRA SLOWED', filename: 'HORIZONTE ULTRA SLOWED.mp3', category: 'chill' },
          
          // Hype
          { id: 'fkngas', name: 'FKNGAS', filename: 'FKNGAS.mp3', category: 'hype' },
          { id: 'fkniggrs_song', name: 'FKNIGGRS SONG', filename: 'FKNIGGRS SONG.mp3', category: 'hype' },
          { id: 'on_that_bxtch', name: 'ON THAT BXTCH!', filename: 'ON THAT BXTCH!mp3.mp3', category: 'hype' },
          { id: 'stay_schemin', name: 'STAY SCHEMMIN', filename: 'STAY SCHEMMIN.mp3', category: 'hype' },
          { id: 'all_10', name: 'ALL 10', filename: 'ALL 10.mp3', category: 'hype' },
          { id: 'spaceship', name: 'SPACESHIP', filename: 'SPACISHIP.mp3', category: 'hype' },
          { id: 'montagem_bilao', name: 'MONTAGEM BILAO', filename: 'MONTAGEM BILAO.mp3', category: 'hype' },
          { id: 'emo_slut', name: 'EMO SLUT', filename: 'EMO SLUT.mp3', category: 'hype' },
          { id: 'evil_tiktok_audio', name: 'EVIL TIKTOK AUDIO', filename: 'EVIL TIKTOK AUDIO.mp3', category: 'hype' },
          { id: 'jay_guapo', name: 'JAY GUAPO', filename: 'JAY GUAPO.mp3', category: 'hype' },
          { id: 'montagem_indeferenca', name: 'MONTAGEM INDEFERENCA', filename: 'MONTAGEM INDEFERENCA.mp3', category: 'hype' },
          { id: 'number_19', name: '#19', filename: '#19.mp3', category: 'hype' },
          // Vibes
          { id: 'funk_mi_camino', name: 'FUNK MI CAMINO', filename: 'FUNK MI CAMINO.mp3', category: 'vibes' },
          { id: 'magic', name: 'MAGIC', filename: 'MAGIC.mp3', category: 'vibes' },
          { id: 'magic_x_maybe_for_you', name: 'MAGIC x MAYBE FOR YOU', filename: 'MAGIC x MAYBE FOR YOU.mp3', category: 'vibes' },
          { id: 'magic_2', name: 'MAGIC 2', filename: 'MAGIC 2.mp3', category: 'vibes' },
          { id: 'maybe_for_you', name: 'MAYBE FOR YOU', filename: 'MAYBE FOR YOU.mp3', category: 'vibes' },
          { id: 'mrs_magic', name: 'MRS. MAGIC', filename: 'MRS. MAGIC.mp3', category: 'vibes' },
          { id: 'mac_demarco', name: 'MAC DEMARCO', filename: 'MAC DEMARCO.mp3', category: 'vibes' },
          { id: 'anotherlife', name: 'ANOTHERLIFE', filename: 'ANOTHERLIFE.mp3', category: 'vibes' },
          { id: 'dream', name: 'DREAM', filename: 'DREAM.mp3', category: 'vibes' },
          { id: 'dream_2', name: 'DREAM 2', filename: 'DREAM 2.mp3', category: 'vibes' },
        ]);
      } catch (error) {
        console.error('Error loading library audio:', error);
      }
    };
    
    loadLibraryAudio();
  }, []);

  // Load user's starred audio preferences
  useEffect(() => {
    if (!user) return;

    const loadStarredAudio = async () => {
      try {
        const audioPrefsRef = doc(db, 'users', user.uid, 'audioPreferences', 'starred');
        const audioPrefsDoc = await getDoc(audioPrefsRef);
        
        if (audioPrefsDoc.exists()) {
          const starredIds = audioPrefsDoc.data().audioIds || [];
          setStarredAudio(starredIds);
        } else {
          setStarredAudio([]);
        }
      } catch (error) {
        console.error('Error loading starred audio:', error);
        setStarredAudio([]);
      }
    };

    loadStarredAudio();
  }, [user]);

  // stop audio when modal is closed
  useEffect(() => {
    if (!showAudioModal && audioRef.current) {
        audioRef.current.pause();
        setPlayingAudioId(null);
    }
  }, [showAudioModal]);

  // Load available circle overlays
  useEffect(() => {
    const loadCircleOverlays = async () => {
      try {
        const response = await fetch('/api/video/circle-overlays');
        if (response.ok) {
          const data = await response.json();
          setAvailableCircles(data.circles || []);
        }
      } catch (error) {
        console.error('Error loading circle overlays:', error);
      }
    };

    loadCircleOverlays();
  }, []);

  // Toggle starred audio (limit 3)
  const toggleStarredAudio = async (audioId) => {
    if (!user) return;

    try {
      const isCurrentlyStarred = starredAudio.includes(audioId);
      let newStarredAudio;

      if (isCurrentlyStarred) {
        // Remove from starred
        newStarredAudio = starredAudio.filter(id => id !== audioId);
      } else {
        // Add to starred (check limit)
        if (starredAudio.length >= 3) {
          setError('You can only star up to 3 audio tracks. Please unstar one first.');
          return;
        }
        newStarredAudio = [...starredAudio, audioId];
      }

      // Update Firebase
      const audioPrefsRef = doc(db, 'users', user.uid, 'audioPreferences', 'starred');
      await setDoc(audioPrefsRef, {
        audioIds: newStarredAudio,
        updatedAt: serverTimestamp()
      }, { merge: true });

      // Update local state
      setStarredAudio(newStarredAudio);
      
    } catch (error) {
      console.error('Error toggling starred audio:', error);
      setError('Failed to update starred audio. Please try again.');
    }
  };

  const updateUserStats = async (newTweetUrl) => {
    if (!user) return;

    const userStatsRef = doc(db, 'users', user.uid);
    const statsDoc = await getDoc(userStatsRef);
    
    if (statsDoc.exists()) {
      const currentStats = statsDoc.data();
      const currentImports = currentStats.recentImports || [];
      
      // Add new import to the beginning and keep only last 5
      let newImports = [
        {
          url: newTweetUrl,
          timestamp: new Date().toISOString()
        },
        ...currentImports
      ].slice(0, 5);

      await updateDoc(userStatsRef, {
        totalVideos: (currentStats.totalVideos || 0) + 1,
        recentImports: newImports
      });
    } else {
      await setDoc(userStatsRef, {
        totalVideos: 1,
        recentImports: [{
          url: newTweetUrl,
          timestamp: new Date().toISOString()
        }]
      });
    }
  };

  // Check if URL has been used before and return count
  const checkForDuplicateVideo = async (tweetUrl) => {
    if (!user) return 0;

    try {
      const allVideosRef = doc(db, 'users', user.uid, 'allVideos', 'videoList');
      const videosDoc = await getDoc(allVideosRef);
      
      if (!videosDoc.exists()) return 0;
      
      const allVideos = videosDoc.data().videos || [];
      const duplicateCount = allVideos.filter(video => video.url === tweetUrl).length;
      
      return duplicateCount;
    } catch (error) {
      console.error('Error checking for duplicates:', error);
      return 0;
    }
  };

  // Log video generation to Firebase (stores ALL videos, not just recent)
  const logVideoGeneration = async (tweetUrl) => {
    if (!user) return;

    try {
      const allVideosRef = doc(db, 'users', user.uid, 'allVideos', 'videoList');
      const videosDoc = await getDoc(allVideosRef);
      
      const newVideoEntry = {
        url: tweetUrl,
        timestamp: new Date().toISOString(),
        createdAt: Date.now() // Use timestamp number instead of serverTimestamp() in array
      };

      if (videosDoc.exists()) {
        const currentVideos = videosDoc.data().videos || [];
        const updatedVideos = [newVideoEntry, ...currentVideos];
        
        await updateDoc(allVideosRef, {
          videos: updatedVideos,
          lastUpdated: serverTimestamp()
        });
      } else {
        await setDoc(allVideosRef, {
          videos: [newVideoEntry],
          lastUpdated: serverTimestamp()
        });
      }
      
      console.log('Video generation logged to Firebase:', tweetUrl);
    } catch (error) {
      console.error('Error logging video generation:', error);
    }
  };

  const handleRecentImportClick = (url) => {
    setTweetUrl(url);
  };

  // Extract tweet ID from various Twitter URL formats
  const extractTweetId = (url) => {
    const patterns = [
      /twitter\.com\/\w+\/status\/(\d+)/,
      /x\.com\/\w+\/status\/(\d+)/,
      /mobile\.twitter\.com\/\w+\/status\/(\d+)/,
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  // Fetch tweet data via Netlify function or local proxy
  const fetchTweetData = async (tweetId) => {
    try {
      const apiUrl = `/api/twitter/tweet`;

      console.log(`Fetching tweet from: ${apiUrl}`);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tweetId }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Netlify Function Error: ${response.status}`
        );
      }

      const data = await response.json();
      console.log('API Response:', data);
      console.log('🔍 Full API Response structure:', JSON.stringify(data, null, 2));
      return data;
      
    } catch (error) {
      console.error('API call failed:', error.message);
      throw error; // Re-throw to be caught by handleGenerateVideo
    }
  };

  // Download profile picture and convert to File object
  const downloadProfilePicture = async (imageUrl) => {
    try {
      // Upgrade to high quality profile picture
      const highResUrl = imageUrl.replace('_normal', '_400x400');
      console.log('Using high-res profile picture:', highResUrl);

      const apiUrl = `/api/download-image`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ imageUrl: highResUrl }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }

      const blob = await response.blob();
      return new File([blob], 'profile.jpg', { type: blob.type });

    } catch (error) {
      console.error('Profile picture download failed:', error);
      return null;
    }
  };

  const handleAudioSelect = (type, file = null, libraryId = '') => {
    console.log('Audio selection changed:', { type, file, libraryId });
    
    setSelectedAudioType(type);
    
    if (type === 'custom' && file) {
      setCustomAudioFile(file);
      setSelectedAudioFile(file);
      setSelectedLibraryAudio('');
    } else if (type === 'library' && libraryId) {
      setSelectedLibraryAudio(libraryId);
      setSelectedAudioFile(null);
      setCustomAudioFile(null);
      
      // Log the selected library audio
      const selectedAudio = availableLibraryAudio.find(a => a.id === libraryId);
      console.log('Selected library audio:', selectedAudio);
    } else {
      // Default
      setSelectedAudioFile(null);
      setCustomAudioFile(null);
      setSelectedLibraryAudio('');
    }
  };

  const handleCircleToggle = (circleId) => {
    setSelectedCircles(prev => {
      if (prev.includes(circleId)) {
        // Remove if already selected
        return prev.filter(id => id !== circleId);
      } else {
        // Add if not selected (max 3 circles)
        if (prev.length < 3) {
          return [...prev, circleId];
        }
        return prev;
      }
    });
  };

  const getCircleInitials = (circleId) => {
    const initials = {
      'donald-trump': 'DT',
      'joe-biden': 'JB',
      'dustin-poirier': 'DP', 
      'khamzat-chimaev': 'KC',
      'conor-mcgregor': 'CM',
      'ronaldo': 'CR',
      'messi': 'LM',
      'andrew-tate': 'AT',
      'tristan-tate': 'TT'
    };
    return initials[circleId] || '??';
  };

  const handleCustomAudioUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('audio/')) {
        setError('Please select a valid audio file (MP3, WAV, etc.)');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('Audio file must be less than 10MB');
        return;
      }
      
      if (!user) {
        setError('You must be logged in to upload audio.');
        return;
      }

      setIsUploading(true);
      setError('');

      try {
        // Create a unique filename to avoid conflicts
        const timestamp = Date.now();
        const fileExtension = file.name.split('.').pop();
        const uniqueFilename = `custom_audio_${timestamp}.${fileExtension}`;
        
        const storageRef = ref(storage, `audio/${user.uid}/${uniqueFilename}`);
        const uploadTask = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(uploadTask.ref);

        // Save to Firestore
        const customAudioRef = collection(db, 'users', user.uid, 'customAudio');
        const docRef = await addDoc(customAudioRef, {
            name: file.name,
            originalName: file.name,
            url: downloadURL,
            filename: uniqueFilename,
            size: file.size,
            type: file.type,
            createdAt: serverTimestamp(),
        });

        // Update local state
        setUserCustomAudio(prev => [...prev, {
          id: docRef.id,
          name: file.name,
          url: downloadURL,
          filename: uniqueFilename,
          createdAt: new Date()
        }]);

        // Set as selected audio
        setSelectedAudioType('custom');
        setCustomAudioFile(file);
        setSelectedAudioFile(file);
        
        console.log('Custom audio uploaded successfully:', {
          name: file.name,
          size: file.size,
          url: downloadURL
        });

      } catch (error) {
          console.error("Error uploading audio:", error);
          setError("Failed to upload custom audio. Please try again.");
      } finally {
          setIsUploading(false);
          event.target.value = null;
      }
    }
  };

  // Delete custom audio from Firestore and Storage
  const handleDeleteCustomAudio = async (audioId, filename) => {
    if (!user || !audioId) return;

    try {
      // Show confirmation dialog
      if (!window.confirm('Are you sure you want to delete this audio file? This action cannot be undone.')) {
        return;
      }

      // Delete from Firestore
      const audioDocRef = doc(db, 'users', user.uid, 'customAudio', audioId);
      await deleteDoc(audioDocRef);

      // Delete from Storage
      if (filename) {
        const storageRef = ref(storage, `audio/${user.uid}/${filename}`);
        try {
          await deleteObject(storageRef);
        } catch (storageError) {
          console.warn('Storage file may not exist:', storageError);
          // Continue even if storage deletion fails (file might not exist)
        }
      }

      // Update local state
      setUserCustomAudio(prev => prev.filter(audio => audio.id !== audioId));

      // If the deleted audio was currently selected, clear the selection
      if (selectedAudioType === 'library' && selectedLibraryAudio === audioId) {
        setSelectedAudioType('');
        setSelectedLibraryAudio('');
      }

      console.log('Custom audio deleted successfully:', audioId);

    } catch (error) {
      console.error('Error deleting custom audio:', error);
      setError('Failed to delete audio file. Please try again.');
    }
  };

  const handleAudioConfirm = () => {
    // Keep the current selection and close the modal
    setShowAudioModal(false);
    
    // Log the final selection
    console.log('Audio selection confirmed:', {
      type: selectedAudioType,
      libraryId: selectedLibraryAudio,
      customFile: customAudioFile,
      selectedFile: selectedAudioFile
    });
  };

  const handleGenerateVideo = async () => {
    console.log('🚨 TESTING: handleGenerateVideo called - changes are working!');
    
    if (!tweetUrl.trim()) {
      setError('Please enter a tweet URL');
      return;
    }

    // Log video generation immediately when generate is clicked
    await logVideoGeneration(tweetUrl);

    // Check for duplicate videos
    const duplicates = await checkForDuplicateVideo(tweetUrl);
    if (duplicates > 1) { // > 1 because we just logged this one
      setDuplicateCount(duplicates - 1); // Don't count the one we just added
      setPendingDuplicateUrl(tweetUrl);
      setShowDuplicateWarning(true);
      return;
    }

    // Extract username from URL FIRST (before any API calls)
    const username = extractUsernameFromUrl(tweetUrl);
    if (!username) {
      setError('Invalid Twitter URL format');
      return;
    }

    console.log('Extracted username from URL:', username);
    console.log('User profile:', userProfile);

    // Check if user can use this username
    if (userProfile && userProfile.isUnlimited) {
      // User is unlimited, proceed directly to video generation
      await generateVideoFromTweet();
      return;
    }

    if (userProfile && userProfile.twitterUsernames?.length > 0) {
      // User already has locked usernames, check if this username is in the list
      const hasAccess = userProfile.twitterUsernames.some(u => u.toLowerCase() === username.toLowerCase());
      if (!hasAccess) {
        // Check if this username has already been used for a sample
        if (hasUsedSample(username)) {
          setError(`You've already used your free sample for @${username}. You can only make videos from @${userProfile.twitterUsernames.join(', @')}.`);
          return;
        }
        // Show sample mode option
        console.log('Showing sample confirmation for username:', username);
        setPendingSampleUsername(username);
        setShowSampleConfirmation(true);
        return;
      }
      // Username is in allowed list, proceed directly to video generation
      await generateVideoFromTweet();
      return;
    }

    // If user hasn't set any Twitter usernames yet (and not unlimited), show confirmation
    if (userProfile && (!userProfile.twitterUsernames || userProfile.twitterUsernames.length === 0) && !userProfile.isUnlimited) {
      console.log('Showing confirmation for username:', username);
      setPendingUsername(username);
      setShowConfirmation(true);
      return;
    }

    // If user is unlimited or has no restrictions, proceed directly
    await generateVideoFromTweet();
  };

  const confirmUsernameRestriction = async () => {
    console.log('User confirmed restriction to username:', pendingUsername);
    setShowConfirmation(false);
    await setUserTwitterUsername(pendingUsername);
    await generateVideoFromTweet();
  };

  const cancelUsernameRestriction = () => {
    console.log('User cancelled username restriction');
    setShowConfirmation(false);
    setPendingUsername('');
  };

  // Sample mode confirmation functions
  const confirmSampleGeneration = async () => {
    console.log('User confirmed sample generation for username:', pendingSampleUsername);
    setShowSampleConfirmation(false);
    await addSampleUsername(pendingSampleUsername);
    await generateVideoFromTweet();
  };

  const cancelSampleGeneration = () => {
    console.log('User cancelled sample generation');
    setShowSampleConfirmation(false);
    setPendingSampleUsername('');
  };

  // Duplicate warning confirmation functions
  const confirmDuplicateGeneration = async () => {
    console.log('User confirmed duplicate generation for:', pendingDuplicateUrl);
    setShowDuplicateWarning(false);
    setPendingDuplicateUrl('');
    setDuplicateCount(0);
    
    // Continue with the original flow
    const username = extractUsernameFromUrl(tweetUrl);
    if (!username) {
      setError('Invalid Twitter URL format');
      return;
    }

    // Check if user can use this username
    if (userProfile && userProfile.twitterUsernames?.length > 0 && !userProfile.isUnlimited) {
      const hasAccess = userProfile.twitterUsernames.some(u => u.toLowerCase() === username.toLowerCase());
      if (!hasAccess) {
        if (hasUsedSample(username)) {
          setError(`You've already used your free sample for @${username}. You can only make videos from @${userProfile.twitterUsernames.join(', @')}.`);
          return;
        }
        setPendingSampleUsername(username);
        setShowSampleConfirmation(true);
        return;
      }
      await generateVideoFromTweet();
      return;
    }

    if (userProfile && (!userProfile.twitterUsernames || userProfile.twitterUsernames.length === 0) && !userProfile.isUnlimited) {
      setPendingUsername(username);
      setShowConfirmation(true);
      return;
    }

    await generateVideoFromTweet();
  };

  const cancelDuplicateGeneration = () => {
    console.log('User cancelled duplicate generation');
    setShowDuplicateWarning(false);
    setPendingDuplicateUrl('');
    setDuplicateCount(0);
  };

  const generateVideoFromTweet = async () => {
    setIsLoading(true);
    setError('');

    try {
      // Prepare audio selection data
      const allLibraryAudio = [...userCustomAudio, ...availableLibraryAudio];
      const audioSelection = {
        type: selectedAudioType,
        file: selectedAudioType === 'custom' ? customAudioFile : null,
        libraryFilename: null,
        libraryUrl: null
      };
      
      if (selectedAudioType === 'library' && selectedLibraryAudio) {
        const audio = allLibraryAudio.find(a => a.id === selectedLibraryAudio);
        if (audio) {
            if (audio.isCustom) {
                audioSelection.libraryUrl = audio.url;
            } else {
                audioSelection.libraryFilename = audio.filename;
            }
        }
      }
      
      console.log('Audio Selection Data:', {
        selectedType: selectedAudioType,
        selectedLibraryAudio,
        audioSelection,
        availableAudio: allLibraryAudio
      });

      // Use the SAME shared processor as bulk generation to ensure consistency
      const importedData = await processTweetData(tweetUrl, audioSelection);
      
      // Verify user permissions after processing
      if (userProfile && userProfile.twitterUsernames?.length > 0 && !userProfile.isUnlimited) {
        const hasAccess = userProfile.twitterUsernames.some(u => u.toLowerCase() === importedData.username.toLowerCase());
        if (!hasAccess) {
          throw new Error(`This tweet is from @${importedData.username}, but you can only make videos from @${userProfile.twitterUsernames.join(', @')}`);
        }
      }

      // Update user stats in Firebase (for recent imports display)
      await updateUserStats(tweetUrl);

      // Add circle overlays to imported data
      importedData.circleOverlays = selectedCircles;
      
      // Add background selection to imported data
      importedData.backgroundSelection = selectedBackground;

      onTweetImported(importedData);
      setTweetUrl(''); // Clear the input
      
    } catch (error) {
      console.error('Import Error:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isLoading && !isGenerating) {
      handleGenerateVideo();
    }
  };

  // Additional URL management functions
  const addUrlField = () => {
    const newId = Date.now(); // Simple unique ID
    setAdditionalUrls([...additionalUrls, { id: newId, url: '' }]);
  };

  const removeUrlField = (id) => {
    setAdditionalUrls(additionalUrls.filter(item => item.id !== id));
  };

  const updateAdditionalUrl = (id, url) => {
    setAdditionalUrls(additionalUrls.map(item => 
      item.id === id ? { ...item, url } : item
    ));
  };

  const handleBulkGeneration = async () => {
    // Collect all URLs (main + additional)
    const allUrls = [tweetUrl, ...additionalUrls.map(item => item.url)].filter(url => url.trim());
    
    if (allUrls.length === 0) {
      setError('Please enter at least one valid tweet URL');
      return;
    }

    const audioSelection = {
      type: selectedAudioType,
      file: selectedAudioType === 'custom' ? customAudioFile : null,
      libraryFilename: selectedAudioType === 'library' && selectedLibraryAudio ? 
        availableLibraryAudio.find(a => a.id === selectedLibraryAudio)?.filename : null,
      libraryUrl: selectedAudioType === 'library' && selectedLibraryAudio ? 
        availableLibraryAudio.find(a => a.id === selectedLibraryAudio)?.url : null
    };

    // Pass multiple URLs to the parent for bulk processing
    onTweetImported({
      type: 'bulk',
      urls: allUrls,
      audioSelection: audioSelection,
      backgroundSelection: selectedBackground,
      circleOverlays: selectedCircles
    });
  };

  const formatTimeAgo = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  // Extract username from Twitter URL
  const extractUsernameFromUrl = (url) => {
    const patterns = [
      /twitter\.com\/(\w+)\/status\/\d+/,
      /x\.com\/(\w+)\/status\/\d+/,
      /mobile\.twitter\.com\/(\w+)\/status\/\d+/,
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  // Add user's Twitter username to their allowed list
  const setUserTwitterUsername = async (username) => {
    if (!user || !username) return;

    const userProfileRef = doc(db, 'users', user.uid);
    
    // Get current usernames and add new one if not already present
    const currentUsernames = userProfile?.twitterUsernames || [];
    if (!currentUsernames.some(u => u.toLowerCase() === username.toLowerCase())) {
      const updatedUsernames = [...currentUsernames, username];
      await updateDoc(userProfileRef, {
        twitterUsernames: updatedUsernames,
        lockedAt: new Date().toISOString()
      });
    }
  };

  // Add user's sample username to track usage
  const addSampleUsername = async (username) => {
    if (!user || !username) return;

    const userProfileRef = doc(db, 'users', user.uid);
    
    // Get current sample usernames and add new one if not already present
    const currentSampleUsernames = userProfile?.sampleUsernames || [];
    if (!currentSampleUsernames.some(u => u.toLowerCase() === username.toLowerCase())) {
      const updatedSampleUsernames = [...currentSampleUsernames, username];
      await updateDoc(userProfileRef, {
        sampleUsernames: updatedSampleUsernames,
        lastSampleAt: new Date().toISOString()
      });
    }
  };

  // Check if username has been used for sample
  const hasUsedSample = (username) => {
    const sampleUsernames = userProfile?.sampleUsernames || [];
    return sampleUsernames.some(u => u.toLowerCase() === username.toLowerCase());
  };

  const toggleAudioPreview = (e, audio) => {
    e.stopPropagation(); // Stop event from bubbling up to the radio button

    if (playingAudioId === audio.id) {
        // Pause current audio
        audioRef.current.pause();
        setPlayingAudioId(null);
    } else {
        // Stop currently playing audio if any
        if (audioRef.current) {
            audioRef.current.pause();
        }
        
        // Play new audio
        let audioPath;
        if (audio.isCustom) {
          audioPath = audio.url;
        } else {
          // Use correct Firebase Storage public URL for all library audio files
          const encodedFileName = encodeURIComponent(audio.filename);
          audioPath = `https://firebasestorage.googleapis.com/v0/b/twizz-f6d50.firebasestorage.app/o/audio%2F${encodedFileName}?alt=media`;
        }
        audioRef.current = new Audio(audioPath);
        audioRef.current.play().catch(err => {
          console.error("Failed to play audio:", err)
          setError("Could not play audio preview.")
        });

        setPlayingAudioId(audio.id);
        
        audioRef.current.onended = () => {
            setPlayingAudioId(null);
        };
    }
  };

  const getCategorizedAudio = () => {
    const categories = {
      trending: availableLibraryAudio.filter(audio => audio.category === 'trending'),
      chill: availableLibraryAudio.filter(audio => audio.category === 'chill'),
      hype: availableLibraryAudio.filter(audio => audio.category === 'hype'),
      vibes: availableLibraryAudio.filter(audio => audio.category === 'vibes'),
    };
    return categories;
  };

  const handleLibrarySelect = (audioId) => {
    setSelectedLibraryAudio(audioId);
    setSelectedAudioType('library');
    setSelectedAudioFile(null);
    setCustomAudioFile(null);
    setShowLibraryModal(false);
  };



  return (
    <div className="dashboard-twitter-importer">
      {/* Audio Selection Modal */}
      {showAudioModal && (
        <div className="audio-modal-overlay" onClick={() => setShowAudioModal(false)}>
          <div className="audio-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🎵 Choose Audio</h3>
              <button 
                className="modal-close"
                onClick={() => setShowAudioModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="audio-options">
              {/* Default Audio */}
              <div 
                className={`audio-option ${selectedAudioType === 'default' ? 'selected' : ''}`}
                onClick={() => handleAudioSelect('default')}
              >
                <div className="audio-icon">🎵</div>
                <div className="audio-details">
                  <div className="audio-name">Default - GRAVE</div>
                  <div className="audio-description">Our signature background music</div>
                </div>
                <div className="audio-radio">
                  <input 
                    type="radio" 
                    name="audio-selection" 
                    checked={selectedAudioType === 'default'}
                    onChange={() => handleAudioSelect('default')}
                  />
                </div>
              </div>

              {/* Starred Audio Quick Select */}
              {starredAudio.length > 0 && (
                <div className="audio-section">
                  <div className="section-title">⭐ Your Favorites ({starredAudio.length}/3)</div>
                  {starredAudio.map((audioId) => {
                    const audio = [...availableLibraryAudio, ...userCustomAudio].find(a => a.id === audioId);
                    if (!audio) return null;
                    
                    return (
                      <div 
                        key={audio.id}
                        className={`audio-option ${selectedAudioType === 'library' && selectedLibraryAudio === audio.id ? 'selected' : ''}`}
                        onClick={() => handleAudioSelect('library', null, audio.id)}
              >
                <button
                  className="audio-play-button"
                  onClick={(e) => {
                    e.stopPropagation();
                            toggleAudioPreview(e, audio);
                  }}
                >
                          {playingAudioId === audio.id ? '⏸' : '▶'}
                </button>
                <div className="audio-details">
                          <div className="audio-name">⭐ {audio.name}</div>
                          <div className="audio-description">Your starred favorite</div>
                </div>
                <div className="audio-radio">
                  <input 
                    type="radio" 
                    name="audio-selection" 
                            checked={selectedAudioType === 'library' && selectedLibraryAudio === audio.id}
                            onChange={() => handleAudioSelect('library', null, audio.id)}
                  />
                </div>
              </div>
                    );
                  })}
                </div>
              )}

              {/* Custom Upload */}
              <div className={`audio-option ${selectedAudioType === 'custom' ? 'selected' : ''}`}>
                <div className="audio-icon">📎</div>
                <div className="audio-details">
                  <div className="audio-name">Upload Custom Audio</div>
                  <div className="audio-description">
                    Upload your own audio file (max 10MB)
                    {customAudioFile && (
                      <div className="custom-file-info">
                        📎 {customAudioFile.name}
                      </div>
                    )}
                  </div>
                </div>
                <div className="audio-action">
                  <input
                    type="file"
                    accept="audio/*"
                    onChange={handleCustomAudioUpload}
                    style={{ display: 'none' }}
                    id="custom-audio-upload"
                    disabled={isUploading}
                  />
                  <label htmlFor="custom-audio-upload" className={`upload-btn ${isUploading ? 'uploading' : ''}`}>
                    {isUploading ? 'Uploading...' : 'Choose File'}
                  </label>
                </div>
              </div>

              {/* User's Custom Audio Library */}
              {userCustomAudio.length > 0 && (
                <div className="audio-section">
                  <div className="section-title">✨ Your Library</div>
                  {userCustomAudio.map((audio) => (
                    <div
                      key={audio.id}
                      className={`audio-option ${selectedAudioType === 'library' && selectedLibraryAudio === audio.id ? 'selected' : ''}`}
                      onClick={() => handleAudioSelect('library', null, audio.id)}
                    >
                      <button
                        className="audio-play-button"
                        onClick={(e) => toggleAudioPreview(e, audio)}
                      >
                        {playingAudioId === audio.id ? '⏸' : '▶'}
                      </button>
                      <div className="audio-details">
                        <div className="audio-name">{audio.name}</div>
                        <div className="audio-description">Custom uploaded audio</div>
                      </div>
                      <button
                        className={`audio-star-button ${starredAudio.includes(audio.id) ? 'starred' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStarredAudio(audio.id);
                        }}
                        title={starredAudio.includes(audio.id) ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        {starredAudio.includes(audio.id) ? '⭐' : '☆'}
                      </button>
                      <button
                        className="audio-delete-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCustomAudio(audio.id, audio.filename);
                        }}
                        title="Delete this audio file"
                      >
                        🗑️
                      </button>
                      <div className="audio-radio">
                        <input
                          type="radio"
                          name="audio-selection"
                          checked={selectedAudioType === 'library' && selectedLibraryAudio === audio.id}
                          onChange={() => handleAudioSelect('library', null, audio.id)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Library Audio - Choose from Library Button */}
              <div className="audio-section">
                <div className="section-title">🎶 Audio Library</div>
                <div 
                  className={`audio-option library-choose-button ${selectedAudioType === 'library' && selectedLibraryAudio ? 'selected' : ''}`}
                  onClick={() => setShowLibraryModal(true)}
                >
                  <div className="audio-icon">🎶</div>
                  <div className="audio-details">
                    <div className="audio-name">
                      {selectedLibraryAudio ? 
                        availableLibraryAudio.find(a => a.id === selectedLibraryAudio)?.name || 'Choose from Library' 
                        : 'Choose from Library'
                      }
                    </div>
                    <div className="audio-description">
                      {selectedLibraryAudio ? 'Library audio selected' : 'Browse categorized audio tracks'}
                    </div>
                  </div>
                  <div className="audio-arrow">→</div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-cancel"
                onClick={() => {
                  // Reset to previous selection
                  setShowAudioModal(false);
                }}
              >
                Cancel
              </button>
              <button 
                className="btn-confirm"
                onClick={handleAudioConfirm}
              >
                Select Audio
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Library Selection Modal */}
      {showLibraryModal && (
        <div className="audio-modal-overlay" onClick={() => setShowLibraryModal(false)}>
          <div className="audio-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🎶 Choose from Library</h3>
              <button 
                className="modal-close"
                onClick={() => setShowLibraryModal(false)}
              >
                ×
              </button>
            </div>
            
            {/* Category Tabs */}
            <div className="category-tabs">
              {Object.entries({
                trending: '🔥 Trending',
                chill: '😌 Chill',
                hype: '⚡ Hype',
                vibes: '🌊 Vibes'
              }).map(([key, label]) => (
                <button
                  key={key}
                  className={`category-tab ${selectedCategory === key ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(key)}
                >
                  {label}
                </button>
              ))}
            </div>
            
            {/* Categorized Audio */}
            <div className="categorized-audio">
              {getCategorizedAudio()[selectedCategory]?.map((audio) => (
                <div 
                  key={audio.id}
                  className={`audio-option ${selectedLibraryAudio === audio.id ? 'selected' : ''}`}
                  onClick={() => handleLibrarySelect(audio.id)}
                >
                  <button 
                    className="audio-play-button"
                    onClick={(e) => toggleAudioPreview(e, audio)}
                  >
                    {playingAudioId === audio.id ? '⏸' : '▶'}
                  </button>
                  <div className="audio-details">
                    <div className="audio-name">{audio.name}</div>
                    <div className="audio-description">Library audio track</div>
                  </div>
                  <button
                    className={`audio-star-button ${starredAudio.includes(audio.id) ? 'starred' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleStarredAudio(audio.id);
                    }}
                    title={starredAudio.includes(audio.id) ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    {starredAudio.includes(audio.id) ? '⭐' : '☆'}
                  </button>
                  <div className="audio-radio">
                    <input 
                      type="radio" 
                      name="library-selection" 
                      checked={selectedLibraryAudio === audio.id}
                      onChange={() => handleLibrarySelect(audio.id)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Username Confirmation Modal */}
      {showConfirmation && (
        <div className="confirmation-modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>🔗 Link Your Account</h3>
            </div>
            <div className="modal-body">
              <p>You'll be linked to this Twitter account:</p>
              <div className="username-highlight">
                @{pendingUsername}
              </div>
              <p className="info-text">
                This helps us provide a better experience and prevents abuse.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-cancel"
                onClick={cancelUsernameRestriction}
              >
                Cancel
              </button>
              <button 
                className="btn-upgrade"
                onClick={() => {
                  // Placeholder for upgrade functionality
                  alert('Upgrade to Pro feature coming soon!');
                }}
              >
                Upgrade to Pro
              </button>
              <button 
                className="btn-confirm"
                onClick={confirmUsernameRestriction}
              >
                Link Account & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sample Confirmation Modal */}
      {showSampleConfirmation && (
        <div className="confirmation-modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>🎬 Generate One Sample</h3>
            </div>
            <div className="modal-body">
              <p>Generate your one free sample video from:</p>
              <div className="username-highlight">
                @{pendingSampleUsername}
              </div>
              <p className="info-text">
                After this sample, you won't be able to create more videos from this account. To create unlimited videos from any account, upgrade to Pro.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-cancel"
                onClick={cancelSampleGeneration}
              >
                Cancel
              </button>
              <button 
                className="btn-upgrade"
                onClick={() => {
                  // Placeholder for upgrade functionality
                  alert('Upgrade to Pro feature coming soon!');
                }}
              >
                Upgrade to Pro
              </button>
              <button 
                className="btn-confirm"
                onClick={confirmSampleGeneration}
              >
                Generate Sample
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Warning Modal */}
      {showDuplicateWarning && (
        <div className="confirmation-modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>⚠️ Duplicate Video Detected</h3>
            </div>
            <div className="modal-body">
                             <p>You've already made this video <strong>{duplicateCount} time{duplicateCount !== 1 ? 's' : ''}</strong>. Are you sure you want to proceed?</p>
               <div className="username-highlight" style={{
                 whiteSpace: 'nowrap',
                 overflow: 'hidden',
                 textOverflow: 'ellipsis',
                 maxWidth: '100%',
                 wordBreak: 'break-all'
               }}>
                 {pendingDuplicateUrl}
               </div>
              <p className="info-text">
                This might be a duplicate tweet or a previous import.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-cancel"
                onClick={cancelDuplicateGeneration}
              >
                Cancel
              </button>
              <button 
                className="btn-confirm"
                onClick={confirmDuplicateGeneration}
              >
                Yes, Generate Anyway
              </button>
            </div>
          </div>
        </div>
             )}

      {/* Circle Selection Modal */}
      {showCircleModal && (
        <div className="circle-modal-overlay" onClick={() => setShowCircleModal(false)}>
          <div className="circle-modal" onClick={(e) => e.stopPropagation()}>
            <div className="circle-modal-header">
              <h3>⭕ Add Floating Circles</h3>
              <button 
                className="circle-modal-close"
                onClick={() => setShowCircleModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="circle-modal-content">
              <p className="circle-modal-description">
                Choose people to add as floating circles (max 3)
              </p>
              
              <div className="circles-grid">
                {availableCircles.map((circle) => (
                  <div
                    key={circle.id}
                    className={`circle-option ${selectedCircles.includes(circle.id) ? 'selected' : ''} ${
                      selectedCircles.length >= 3 && !selectedCircles.includes(circle.id) ? 'disabled' : ''
                    }`}
                                         onClick={() => selectedCircles.length < 3 || selectedCircles.includes(circle.id) ? handleCircleToggle(circle.id) : null}
                  >
                                         <div className="circle-avatar">
                       <div className="circle-initials">{getCircleInitials(circle.id)}</div>
                       {selectedCircles.includes(circle.id) && (
                         <div className="selected-indicator">✓</div>
                       )}
                     </div>
                    <div className="circle-name">{circle.displayName}</div>
                  </div>
                ))}
              </div>
              
              <div className="selection-status">
                <span className="selection-count">{selectedCircles.length}/3 selected</span>
                {selectedCircles.length >= 3 && (
                  <span className="max-notice">Maximum reached</span>
                )}
              </div>
            </div>

            <div className="circle-modal-actions">
              <button 
                className="circle-btn-cancel"
                onClick={() => setShowCircleModal(false)}
              >
                Cancel
              </button>
              <button 
                className="circle-btn-confirm"
                onClick={() => setShowCircleModal(false)}
              >
                Confirm Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Background Selection Modal */}
      <BackgroundSelector
        selectedBackground={selectedBackground}
        onBackgroundSelect={setSelectedBackground}
        isVisible={showBackgroundModal}
        onClose={() => setShowBackgroundModal(false)}
      />

      <div className="dashboard-content">
        <div className="content-header">
          <h1 className="page-title">Generate Video</h1>
          <p className="page-description">Import a tweet and create a viral video</p>
        </div>

        <div className="import-section">
          <div className="section-card">
            <div className="card-header">
              <h3 className="card-title">🔗 Import Tweet</h3>
              <p className="card-description">Paste any Twitter/X URL to get started</p>
              
              {/* Show current account status */}
              {userProfile && (userProfile.isUnlimited || (userProfile.twitterUsernames && userProfile.twitterUsernames.length > 0)) && (
                <div className="account-status">
                  {userProfile.isUnlimited ? (
                    <div className="status-badge pro">
                      ✨ Pro Account - Unlimited Access
                    </div>
                  ) : (
                    <div className="status-badge linked">
                      🔗 Linked to @{userProfile.twitterUsernames.join(', @')}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="import-form">
              <div className="form-group">
                <label className="form-label">Tweet URL</label>
                <div className="input-group">
                  <input
                    type="text"
                    value={tweetUrl}
                    onChange={(e) => setTweetUrl(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="https://twitter.com/username/status/1234567890"
                    className="form-input"
                    disabled={isLoading || isGenerating}
                  />
                  
                  {/* Plus Button to add more URLs */}
                  <button
                    className="plus-button"
                    onClick={addUrlField}
                    disabled={isLoading || isGenerating}
                    title="Add another URL for bulk generation"
                    style={{
                      padding: '12px',
                      background: '#333',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      fontWeight: 'bold'
                    }}
                  >
                    +
                  </button>
                  
                  {/* Audio Selection Button */}
                  <button
                    className="audio-button"
                    onClick={() => setShowAudioModal(true)}
                    disabled={isLoading || isGenerating}
                    title="Choose Audio"
                  >
                    <span className="audio-icon">🎵</span>
                  </button>

                  {/* Background Selection Button */}
                  <button
                    className="audio-button background-button"
                    onClick={() => setShowBackgroundModal(true)}
                    disabled={isLoading || isGenerating}
                    title="Choose Background"
                  >
                    <span className="background-icon">🎬</span>
                  </button>

                  {/* Circle Selection Button */}
                  <button
                    className="audio-button circle-button"
                    onClick={() => setShowCircleModal(true)}
                    disabled={isLoading || isGenerating}
                    title="Add Floating Circles"
                  >
                    <span className="circle-icon">⭕</span>
                    {selectedCircles.length > 0 && (
                      <span className="circle-count">{selectedCircles.length}</span>
                    )}
                  </button>
                  
                  <button
                    onClick={additionalUrls.length > 0 ? handleBulkGeneration : handleGenerateVideo}
                    disabled={isLoading || isGenerating || (!tweetUrl.trim() && additionalUrls.filter(item => item.url.trim()).length === 0)}
                    className={`import-button ${isLoading ? 'loading' : ''}`}
                  >
                    {isLoading ? (
                      <>
                        <span className="button-spinner"></span>
                        Processing...
                      </>
                    ) : (
                      <>
                        <span className="button-icon">⚡</span>
                        Generate {additionalUrls.length > 0 ? `${[tweetUrl, ...additionalUrls.map(item => item.url)].filter(url => url.trim()).length} Videos` : ''}
                      </>
                    )}
                  </button>
                </div>

                {/* Additional URL inputs */}
                {additionalUrls.map((item, index) => (
                  <div key={item.id} className="input-group" style={{ marginTop: '8px' }}>
                    <input
                      type="text"
                      value={item.url}
                      onChange={(e) => updateAdditionalUrl(item.id, e.target.value)}
                      placeholder={`Tweet URL ${index + 2}`}
                      className="form-input"
                      disabled={isLoading || isGenerating}
                    />
                    
                    {/* Plus button on the last additional field */}
                    {index === additionalUrls.length - 1 && (
                      <button
                        className="plus-button"
                        onClick={addUrlField}
                        disabled={isLoading || isGenerating}
                        title="Add another URL"
                        style={{
                          padding: '12px',
                          background: '#333',
                          border: 'none',
                          borderRadius: '8px',
                          color: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '18px',
                          fontWeight: 'bold'
                        }}
                      >
                        +
                      </button>
                    )}
                    
                    {/* Remove button for additional fields */}
                    <button
                      className="remove-button"
                      onClick={() => removeUrlField(item.id)}
                      disabled={isLoading || isGenerating}
                      title="Remove this URL"
                      style={{
                        padding: '12px',
                        background: '#dc3545',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '18px',
                        fontWeight: 'bold'
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
                
                {/* Audio Selection Display */}
                {selectedAudioType !== 'default' && (
                  <div className="audio-selection-display">
                    <span className="audio-indicator">
                      🎵 Audio: {
                        selectedAudioType === 'custom' 
                          ? customAudioFile?.name || 'Custom Upload'
                          : selectedAudioType === 'library' 
                            ? availableLibraryAudio.find(a => a.id === selectedLibraryAudio)?.name || 'Library Audio'
                            : 'Default - GRAVE'
                      }
                    </span>
                    <button 
                      className="change-audio-btn"
                      onClick={() => setShowAudioModal(true)}
                    >
                      Change
                    </button>
                  </div>
                )}

                {/* Circle Selection Display */}
                {selectedCircles.length > 0 && (
                  <div className="circle-selection-display">
                    <span className="circle-indicator">
                      ⭕ Circles: {selectedCircles.map(circleId => 
                        availableCircles.find(c => c.id === circleId)?.displayName
                      ).filter(Boolean).join(', ')}
                    </span>
                    <button 
                      className="change-circle-btn"
                      onClick={() => setShowCircleModal(true)}
                    >
                      Change
                    </button>
                  </div>
                )}

                {/* Background Selection Display */}
                {selectedBackground?.type !== 'black' && (
                  <div className="background-selection-display">
                    <span className="background-indicator">
                      🎬 Background: {selectedBackground?.type === 'video' ? 'Custom Video' : 'Black'}
                    </span>
                    <button 
                      className="change-background-btn"
                      onClick={() => setShowBackgroundModal(true)}
                    >
                      Change
                    </button>
                  </div>
                )}
                
                {error && (
                  <div className="error-alert">
                    <span className="alert-icon">⚠️</span>
                    <span>{error}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="stats-section">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <div className="stat-number">{userStats.totalVideos}</div>
                <div className="stat-label">Videos Generated</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⚡</div>
              <div className="stat-content">
                <div className="stat-number">{userStats.avgGenerationTime}</div>
                <div className="stat-label">Avg. Generation Time</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🎯</div>
              <div className="stat-content">
                <div className="stat-number">9:16</div>
                <div className="stat-label">Perfect Aspect Ratio</div>
              </div>
            </div>
          </div>
        </div>

        <div className="recent-section">
          <div className="section-card">
            <div className="card-header">
              <h3 className="card-title">🐦 Recent Imports</h3>
              <p className="card-description">Your last 5 tweet imports</p>
            </div>
            
            <div className="recent-list">
              {userStats.recentImports.length > 0 ? (
                userStats.recentImports.map((item, index) => (
                  <div key={index} className="recent-item">
                    <div className="recent-icon">🐦</div>
                    <div className="recent-content">
                      <div className="recent-url">{item.url}</div>
                      <div className="recent-time">{formatTimeAgo(item.timestamp)}</div>
                    </div>
                    <button 
                      className="recent-action" 
                      title="Use this URL"
                      onClick={() => handleRecentImportClick(item.url)}
                    >
                      ↻
                    </button>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <p>No recent imports yet. Start by generating your first video!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  };
  
export default TwitterImporter; 